import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const NAV_LINKS = [
  { label: "The Vision", href: "#paradox", testid: "nav-vision" },
  { label: "Experience", href: "#experience", testid: "nav-experience" },
  { label: "Platform", href: "#platform", testid: "nav-platform" },
  { label: "Contact", href: "#contact", testid: "nav-contact-link" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const scrollTo = (href) => (e) => {
    e.preventDefault();
    // Close menu first, then scroll after the exit animation so the collapsing
    // menu height doesn't cancel the browser's pending smooth scroll.
    const menuWasOpen = open;
    setOpen(false);
    const doScroll = () => {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    if (menuWasOpen) {
      // Wait for AnimatePresence exit (~300ms) before scrolling
      setTimeout(doScroll, 320);
    } else {
      doScroll();
    }
  };

  return (
    <motion.header
      data-testid="main-nav"
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-[90] transition-colors duration-500 ${
        scrolled ? "bg-obsidian/70 backdrop-blur-xl border-b border-gold/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto flex items-center justify-between px-6 md:px-12 py-5">
        <a
          href="#top"
          onClick={scrollTo("#top")}
          data-testid="nav-logo"
          className="group flex items-center gap-3"
        >
          <span className="w-7 h-7 border border-gold/50 rounded-full grid place-items-center">
            <span className="w-2 h-2 rounded-full bg-gold shadow-[0_0_10px_rgba(212,175,55,0.8)]" />
          </span>
          <span className="font-display text-xl tracking-wide text-bone">
            Replay<span className="text-gold italic">Era</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={scrollTo(l.href)}
              data-testid={l.testid}
              className="font-mono-accent text-[11px] text-bone/70 hover:text-gold transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <a
          href="#contact"
          onClick={scrollTo("#contact")}
          data-testid="nav-contact-cta"
          className="hidden md:inline-flex items-center gap-2 border border-gold/40 text-bone hover:text-obsidian hover:bg-gold px-5 py-2.5 rounded-full font-mono-accent text-[11px] transition-colors duration-300"
        >
          Enquire · <span className="text-gold group-hover:text-obsidian">↗</span>
        </a>

        <button
          type="button"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden w-9 h-9 grid place-items-center border border-gold/30 rounded-full"
          aria-label="Toggle menu"
        >
          <span className={`block w-4 h-px bg-bone transition-transform ${open ? "translate-y-0 rotate-45" : "-translate-y-1"}`} />
          <span className={`block w-4 h-px bg-bone transition-transform ${open ? "-translate-y-px -rotate-45" : "translate-y-1"}`} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-obsidian/95 backdrop-blur-xl border-t border-gold/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={scrollTo(l.href)}
                  data-testid={`mobile-${l.testid}`}
                  className="font-display text-2xl text-bone hover:text-gold"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={scrollTo("#contact")}
                data-testid="mobile-nav-contact"
                className="mt-2 inline-flex items-center gap-2 border border-gold/40 text-bone px-5 py-2.5 rounded-full font-mono-accent text-[11px] w-fit"
              >
                Enquire ↗
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
